/**
 * Family connections — the Clinic Backend surface (ZOY-662/663, verified
 * against cms-backend `docs/api/family-connections.md` at f77b0eb).
 *
 * Auth owns the relationship (invitations, family requests, connections,
 * keyed by the Clinic Patient UUID). The clinic backend orchestrates the
 * employee's actions with its own co-credential and keeps a PROJECTION for
 * display: potentially stale, eventually reconciled, never an authorization
 * answer — which is why every read carries `lastSyncedAt`. This frontend
 * never calls Auth or Plan for this feature.
 */

import { apiRequest } from '../api/http';
import { buildQueryParam, type PagedResult, type QuerySpecInput } from '../api/querySpec';

const enc = encodeURIComponent;

export const FAMILY_LINK_KINDS = ['invitation', 'request', 'connection'] as const;
export type FamilyLinkKind = (typeof FAMILY_LINK_KINDS)[number];

/** Auth's lifecycle vocabulary, mirrored verbatim by the projection. */
export const FAMILY_STATUSES = [
  'pending',
  'accepted',
  'declined',
  'expired',
  'cancelled',
  'withdrawn',
  'superseded',
  'ended',
] as const;
export type FamilyStatus = (typeof FAMILY_STATUSES)[number] | (string & {});

/** This side's write lifecycle for a row — never Auth's status. */
export type FamilyLocalState = 'creating' | 'synced' | 'failed' | (string & {});

export const FAMILY_DECLINE_REASONS = [
  'wrong_number',
  'not_now',
  'unknown_clinic',
  'other',
] as const;
export type FamilyDeclineReason = (typeof FAMILY_DECLINE_REASONS)[number];

export interface FamilyPatientBrief {
  id: string;
  firstName: string;
  lastName: string;
  recordNumber: number | null;
}

/** One projection row: an invitation, a family request, or a connection. */
export interface FamilyLinkView {
  id: string;
  kind: FamilyLinkKind;
  patientId: string | null;
  /** Present only when the caller holds clinic.patients.read. */
  patient?: FamilyPatientBrief | null;
  authRef: string | null;
  localState: FamilyLocalState;
  status: FamilyStatus;
  mobileUsed: string | null;
  note: string | null;
  message: string | null;
  requesterDisplayName: string | null;
  requesterMobile: string | null;
  /** The accepting account's display name — disclosed by Auth after acceptance. */
  accountDisplayName: string | null;
  connectionRef: string | null;
  declineReason: FamilyDeclineReason | string | null;
  declineNote: string | null;
  expiresAt: string | null;
  sendCount: number;
  lastSentAt: string | null;
  acceptedAt: string | null;
  endedAt: string | null;
  /** `clinic_ended` | `family_ended` (Auth's word). */
  endedReason: string | null;
  createdBy: string | null;
  failureReason: string | null;
  lastSyncedAt: string | null;
  clientRef: string | null;
  /** Auth's opaque account uid, verbatim — makes A → B → A legible; never a name. */
  accountRef: string | null;
  authUpdatedAt: string | null;
  endedBy: string | null;
  /** ZOY-711: the Follow-ups record this request was added to; null until "Add to CRM" (always null for invitations/connections). */
  crmRecordId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PatientFamilyPanel {
  patientId: string;
  registrationState: 'guest' | 'registered' | 'merged';
  /** The newest connection the projection believes is accepted — display only. */
  currentConnection: FamilyLinkView | null;
  /** Every connection ever, newest first. */
  connections: FamilyLinkView[];
  /** False while more than one row still reads `accepted` (an end not yet reconciled). */
  projectionConsistent: boolean;
  links: FamilyLinkView[];
  lastSyncedAt: string | null;
}

export const FamilyErrorCode = {
  Unavailable: 'FAMILY_CONNECTIONS_UNAVAILABLE',
  PatientNotRegistered: 'FAMILY_PATIENT_NOT_REGISTERED',
  MobileNotOnRecord: 'FAMILY_MOBILE_NOT_ON_RECORD',
  MobileNotCanonical: 'PATIENT_MOBILE_NOT_CANONICAL',
  PatientAlreadyConnected: 'PATIENT_ALREADY_CONNECTED',
  QuotaExceeded: 'FAMILY_INVITATION_QUOTA_EXCEEDED',
  LinkNotFound: 'FAMILY_LINK_NOT_FOUND',
  LinkInvalidState: 'FAMILY_LINK_INVALID_STATE',
  AuthRefused: 'FAMILY_AUTH_REFUSED',
  AuthRejected: 'FAMILY_AUTH_REJECTED',
  ClinicInactive: 'CLINIC_INACTIVE',
} as const;

/** `GET /v1/patients/:id/family` — reading it also asks for a reconciliation. */
export const fetchPatientFamily = (patientId: string) =>
  apiRequest<PatientFamilyPanel>({ base: 'clinic', path: `/v1/patients/${enc(patientId)}/family` });

/** `POST /v1/patients/:id/family/invitations` — a number the record already holds, E.164. */
export const inviteFamily = (patientId: string, body: { mobile: string; note?: string }) =>
  apiRequest<FamilyLinkView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${enc(patientId)}/family/invitations`,
    body,
  });

/** `GET /v1/family/links?query=` — the clinic-wide workspace (query contract). */
export const listFamilyLinks = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<FamilyLinkView>>({
    base: 'clinic',
    path: '/v1/family/links',
    query: { query: buildQueryParam(spec) },
  });

/** `GET /v1/family/requests` — pending family-initiated requests (≤ 100, with the total). */
export const listFamilyRequests = () =>
  apiRequest<PagedResult<FamilyLinkView>>({ base: 'clinic', path: '/v1/family/requests' });

/** `POST /v1/family/links/refresh` (202) — ask for a reconciliation now. */
export const refreshFamilyLinks = () =>
  apiRequest<{ accepted: true }>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/family/links/refresh',
    body: {},
  });

const action = (linkId: string, verb: 'resend' | 'cancel' | 'end') =>
  apiRequest<FamilyLinkView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/family/links/${enc(linkId)}/${verb}`,
    body: {},
  });

export const resendFamilyInvitation = (linkId: string) => action(linkId, 'resend');
export const cancelFamilyInvitation = (linkId: string) => action(linkId, 'cancel');
export const endFamilyConnection = (linkId: string) => action(linkId, 'end');

/** Staff choose the registered patient EXPLICITLY — the body never carries a name. */
export const acceptFamilyRequest = (linkId: string, patientId: string) =>
  apiRequest<FamilyLinkView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/family/requests/${enc(linkId)}/accept`,
    body: { patientId },
  });

export const declineFamilyRequest = (
  linkId: string,
  body: { reason: FamilyDeclineReason; note?: string },
) =>
  apiRequest<FamilyLinkView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/family/requests/${enc(linkId)}/decline`,
    body,
  });
